import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Loader2, LogOut, RefreshCw, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const STATUS_LABELS = { new: "New", contacted: "Contacted", in_progress: "In Progress", completed: "Completed" };
const STATUS_STYLES = {
  new: "border-gold/50 bg-gold/10 text-gold-dark",
  contacted: "border-sky-300 bg-sky-50 text-sky-700",
  in_progress: "border-amber-300 bg-amber-50 text-amber-700",
  completed: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

const field = "h-11 rounded-lg border-slate-300 bg-white focus-visible:ring-gold";

const Overview = ({ stats }) => {
  if (!stats) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /></div>;
  const cards = [
    { label: "Total Enquiries", value: stats.total, testid: "stat-total" },
    { label: "New", value: stats.by_status.new, testid: "stat-new" },
    { label: "In Progress", value: stats.by_status.in_progress, testid: "stat-in-progress" },
    { label: "Completed", value: stats.by_status.completed, testid: "stat-completed" },
  ];
  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={c.testid}>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{c.label}</p>
            <p className="mt-3 font-serif text-4xl font-bold text-navy-900">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="stat-top-services">
        <h3 className="font-serif text-xl font-bold text-navy-900">Most Requested Services</h3>
        {stats.top_services.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No enquiries yet — they'll appear here as they arrive.</p>
        ) : (
          <div className="mt-5 space-y-4">
            {stats.top_services.map((s) => (
              <div key={s._id}>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold text-navy-900">{s._id}</span>
                  <span className="text-slate-500">{s.count}</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-gold" style={{ width: `${Math.min(100, (s.count / stats.total) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { content, refresh } = useSite();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loadingEq, setLoadingEq] = useState(true);
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [draft, setDraft] = useState(null);
  const [savingContent, setSavingContent] = useState(false);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadEnquiries = useCallback(() => {
    setLoadingEq(true);
    api
      .get("/admin/enquiries", { params: { search: search || undefined, service: serviceFilter, status: statusFilter } })
      .then((r) => setEnquiries(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingEq(false));
  }, [search, serviceFilter, statusFilter]);

  useEffect(() => {
    if (user) {
      loadStats();
      loadEnquiries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, serviceFilter, statusFilter]);

  useEffect(() => {
    if (content) setDraft(content);
  }, [content]);

  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  if (user === false) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
      setSelected((s) => (s && s.id === id ? data : s));
      loadStats();
      toast.success(`Marked as ${STATUS_LABELS[status]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    setSavingNote(true);
    try {
      const { data } = await api.patch(`/admin/enquiries/${selected.id}`, { note: note.trim() });
      setEnquiries((list) => list.map((e) => (e.id === selected.id ? data : e)));
      setSelected(data);
      setNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNote(false);
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/admin/content", draft);
      refresh();
      toast.success("Website content updated successfully.");
    } catch (e) {
      toast.error(formatApiError(e, "Could not save content."));
    } finally {
      setSavingContent(false);
    }
  };

  const setD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setSocial = (k, v) => setDraft((d) => ({ ...d, social: { ...d.social, [k]: v } }));
  const setService = (i, k, v) =>
    setDraft((d) => ({ ...d, services: d.services.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-gold/20 bg-navy-900">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden rounded-full border border-gold/40 bg-gold/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-gold sm:block">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-gold" data-testid="admin-view-site-link">
              <ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">View Site</span>
            </Link>
            <span className="hidden text-sm text-slate-400 md:block">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <Tabs defaultValue="overview">
          <TabsList className="bg-white">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content">Website Content</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-8">
            <Overview stats={stats} />
          </TabsContent>

          <TabsContent value="enquiries" className="mt-8">
            <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center">
              <form
                className="relative flex-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  loadEnquiries();
                }}
              >
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="enquiry-search-input"
                  className={`${field} pl-10`}
                  placeholder="Search by customer name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </form>
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger className={`${field} sm:w-56`} data-testid="enquiry-service-filter">
                  <SelectValue placeholder="All services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {(content?.services || []).map((s) => (
                    <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                  ))}
                  <SelectItem value="General Enquiry">General Enquiry</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`${field} sm:w-44`} data-testid="enquiry-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={loadEnquiries}
                data-testid="enquiry-refresh-btn"
                className="flex items-center justify-center gap-2 rounded-full border border-navy-900 px-5 py-2.5 text-sm font-semibold text-navy-900 transition-colors hover:bg-navy-900 hover:text-gold"
              >
                <RefreshCw className="h-4 w-4" /> Refresh
              </button>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="enquiry-table">
              {loadingEq ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /></div>
              ) : enquiries.length === 0 ? (
                <p className="py-16 text-center text-sm text-slate-500">No enquiries found. New submissions will appear here.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 font-mono text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="px-5 py-3.5">Date</th>
                        <th className="px-5 py-3.5">Customer</th>
                        <th className="px-5 py-3.5">Service</th>
                        <th className="px-5 py-3.5">Contact</th>
                        <th className="px-5 py-3.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enquiries.map((e, i) => (
                        <tr
                          key={e.id}
                          data-testid={`enquiry-row-${i}`}
                          className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-gold/5"
                          onClick={() => {
                            setSelected(e);
                            setNote("");
                          }}
                        >
                          <td className="whitespace-nowrap px-5 py-4 text-slate-500">
                            {new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          </td>
                          <td className="px-5 py-4">
                            <p className="font-semibold text-navy-900">{e.full_name}</p>
                            {e.company_name && <p className="text-xs text-slate-500">{e.company_name}</p>}
                          </td>
                          <td className="px-5 py-4 text-slate-700">{e.service}</td>
                          <td className="px-5 py-4">
                            <p className="text-slate-700">{e.phone}</p>
                            <p className="text-xs text-slate-500">{e.email}</p>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLES[e.status] || ""}`}>
                              {STATUS_LABELS[e.status] || e.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-8">
            {!draft ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /></div>
            ) : (
              <div className="space-y-8">
                <div className="rounded-2xl border border-slate-200 bg-white p-7">
                  <h3 className="font-serif text-xl font-bold text-navy-900">Contact Information</h3>
                  <p className="mt-1 text-sm text-slate-500">These details appear across the website. Replace the placeholders with your real business details.</p>
                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-navy-900">Phone Number</Label>
                      <Input className={field} data-testid="content-phone-input" value={draft.phone} onChange={(e) => setD("phone", e.target.value)} />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-navy-900">WhatsApp Number (with country code)</Label>
                      <Input className={field} data-testid="content-whatsapp-input" value={draft.whatsapp} onChange={(e) => setD("whatsapp", e.target.value)} />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-navy-900">Business Email</Label>
                      <Input className={field} data-testid="content-email-input" value={draft.email} onChange={(e) => setD("email", e.target.value)} />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-navy-900">Business Hours</Label>
                      <Input className={field} data-testid="content-hours-input" value={draft.hours} onChange={(e) => setD("hours", e.target.value)} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="mb-1.5 block text-sm font-semibold text-navy-900">Office Address</Label>
                      <Input className={field} data-testid="content-address-input" value={draft.address} onChange={(e) => setD("address", e.target.value)} />
                    </div>
                  </div>
                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    {["facebook", "instagram", "linkedin", "twitter"].map((k) => (
                      <div key={k}>
                        <Label className="mb-1.5 block text-sm font-semibold capitalize text-navy-900">{k} URL</Label>
                        <Input className={field} value={draft.social?.[k] || ""} onChange={(e) => setSocial(k, e.target.value)} placeholder="https://..." />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-7">
                  <h3 className="font-serif text-xl font-bold text-navy-900">Company Story & About</h3>
                  <p className="mt-1 text-sm text-slate-500">Shown on the homepage and About page. Separate story paragraphs with a blank line.</p>
                  <div className="mt-6 space-y-5">
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-navy-900">About Summary</Label>
                      <Textarea className="min-h-[90px] rounded-lg border-slate-300" data-testid="content-about-input" value={draft.about_short} onChange={(e) => setD("about_short", e.target.value)} />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-navy-900">Company Story</Label>
                      <Textarea className="min-h-[180px] rounded-lg border-slate-300" data-testid="content-story-input" value={draft.story} onChange={(e) => setD("story", e.target.value)} />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-3">
                      {[
                        ["hero_image", "Hero Image URL"],
                        ["about_image_1", "About Image 1 URL"],
                        ["about_image_2", "About Image 2 URL"],
                      ].map(([k, label]) => (
                        <div key={k}>
                          <Label className="mb-1.5 block text-sm font-semibold text-navy-900">{label}</Label>
                          <Input className={field} value={draft[k]} onChange={(e) => setD(k, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-7">
                  <h3 className="font-serif text-xl font-bold text-navy-900">Service Descriptions</h3>
                  <p className="mt-1 text-sm text-slate-500">Edit the text shown for each of the nine services. List offerings one per line.</p>
                  <Accordion type="single" collapsible className="mt-6">
                    {draft.services.map((s, i) => (
                      <AccordionItem key={s.slug} value={s.slug} data-testid={`content-service-${s.slug}`}>
                        <AccordionTrigger className="text-sm font-bold text-navy-900">{s.title}</AccordionTrigger>
                        <AccordionContent className="space-y-4 pt-2">
                          <div>
                            <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Short description (cards)</Label>
                            <Textarea className="min-h-[70px] rounded-lg border-slate-300" value={s.short} onChange={(e) => setService(i, "short", e.target.value)} />
                          </div>
                          <div>
                            <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Overview (services page)</Label>
                            <Textarea className="min-h-[70px] rounded-lg border-slate-300" value={s.overview} onChange={(e) => setService(i, "overview", e.target.value)} />
                          </div>
                          <div>
                            <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Key offerings (one per line)</Label>
                            <Textarea
                              className="min-h-[100px] rounded-lg border-slate-300"
                              value={s.offerings.join("\n")}
                              onChange={(e) => setService(i, "offerings", e.target.value.split("\n").filter((l) => l.trim()))}
                            />
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>

                <button
                  onClick={saveContent}
                  disabled={savingContent}
                  data-testid="save-content-btn"
                  className="flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
                >
                  {savingContent && <Loader2 className="h-4 w-4 animate-spin" />}
                  {savingContent ? "Saving..." : "Save All Changes"}
                </button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="enquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-xl text-navy-900">{selected.full_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs uppercase tracking-wider text-slate-500">Service</p><p className="font-semibold text-navy-900">{selected.service}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-slate-500">Location</p><p className="font-semibold text-navy-900">{selected.location || "—"}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-slate-500">Phone</p><a href={`tel:${selected.phone}`} className="font-semibold text-navy-900 hover:text-gold-dark">{selected.phone}</a></div>
                  <div><p className="text-xs uppercase tracking-wider text-slate-500">Email</p><a href={`mailto:${selected.email}`} className="break-all font-semibold text-navy-900 hover:text-gold-dark">{selected.email}</a></div>
                  {selected.company_name && <div className="col-span-2"><p className="text-xs uppercase tracking-wider text-slate-500">Company</p><p className="font-semibold text-navy-900">{selected.company_name}</p></div>}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Message</p>
                  <p className="mt-1 rounded-lg bg-slate-50 p-4 leading-relaxed text-slate-700">{selected.message}</p>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Status</Label>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger className={field} data-testid="enquiry-detail-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Internal Notes</p>
                  {selected.notes?.length > 0 && (
                    <ul className="mb-3 space-y-2">
                      {selected.notes.map((n, i) => (
                        <li key={i} className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="text-slate-700">{n.text}</p>
                          <p className="mt-1 text-[11px] text-slate-400">{new Date(n.at).toLocaleString("en-IN")}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2">
                    <Input className={field} data-testid="enquiry-note-input" placeholder="Add an internal note..." value={note} onChange={(e) => setNote(e.target.value)} />
                    <button
                      onClick={addNote}
                      disabled={savingNote || !note.trim()}
                      data-testid="enquiry-add-note-btn"
                      className="shrink-0 rounded-full bg-navy-900 px-5 text-sm font-semibold text-gold transition-colors hover:bg-navy-800 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
